import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Download,
  FileSpreadsheet,
  FileText,
  FileType,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  downloadCSV,
  downloadExcel,
  formatReportForExport,
  mergeExportRowsWithComparison,
} from '../../utils/reportExport';
import { captureElement, buildWysiwygPdf, savePdf } from '../../utils/wysiwygPdf';
import { multiColumnExportRows } from '../../utils/reportNormalize';

// Human-friendly period label
const fmtDay = (d) => {
  try {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return d;
  }
};

const fmtPeriod = (period) => {
  if (!period) return null;
  if (period.start && period.end) {
    const f = (d) => {
      try {
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return d;
        return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_) {
        return d;
      }
    };
    return `${f(period.start)} – ${f(period.end)}`;
  }
  if (typeof period === 'string') return period;
  return null;
};

/**
 * Unified Report Export button - produces correctly-formatted CSV / Excel / PDF
 * that mirrors the on-screen report.
 *
 * @param {Object} props
 * @param {Object} props.data         The full reportData object as fetched from the API.
 * @param {string} props.reportType   e.g. 'pnl', 'balance_sheet', 'cash_flow', 'equity',
 *                                    'trial_balance', 'tax_report', 'vat_return',
 *                                    'wht_remittance', 'fx_gainloss'.
 * @param {string} props.reportName   Display name used in the file name & PDF title.
 * @param {Object} props.period       { start, end } date range.
 * @param {string} props.currencySymbol  e.g. "₦"
 * @param {string} props.businessName Optional subtitle on the PDF.
 */
const ReportExportButton = ({
  data,
  reportType,
  reportName = 'Report',
  period = null,
  currencySymbol = '',
  currency: currencyProp = '',
  comparisonData = null,
  comparisonLabel = 'Comparison',
  businessName = '',
  variant = 'outline',
  size = 'sm',
  className = '',
  captureRef = null,
  multiColumns = null,
  cogsLabel = 'Cost of Sales',
}) => {
  const [exporting, setExporting] = useState(null);

  const baseFilename = () => {
    const safe = String(reportName).replace(/\s+/g, '_').replace(/[^\w\-]/g, '');
    const range = period?.start && period?.end ? `_${period.start}_to_${period.end}` : `_${new Date().toISOString().split('T')[0]}`;
    return `${safe}${range}`;
  };

  // Presentation currency: the API converts every figure when a display currency is selected;
  // exports must label (and stamp) that currency instead of the book currency.
  const presentedCurrency = data?.display_currency || data?.currency || currencyProp || undefined;
  const isConverted = !!(presentedCurrency && data?.original_currency && presentedCurrency !== data.original_currency);
  const fxNote = isConverted
    ? `Presented in ${presentedCurrency} — converted from ${data.original_currency} at 1 ${data.original_currency} = ${Number(data.fx_rate || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })} ${presentedCurrency} (closing rate on export date)`
    : undefined;
  const stampCurrency = (rows) => (rows || []).map((r) => ({ ...r, Currency: presentedCurrency || '' }));
  // Comparison (previous period / budget): add comparison, variance and variance % columns for every amount.
  // Multi-column statements (monthly trend / columns by project): one column per month or project.
  const withComparisonRows = (rows) => {
    if (multiColumns && multiColumns.length) return multiColumnExportRows(reportType, multiColumns, { cogsLabel });
    return comparisonData
      ? mergeExportRowsWithComparison(rows, formatReportForExport(comparisonData, reportType), comparisonLabel)
      : rows;
  };
  const baseFilenameCcy = () => (presentedCurrency ? `${baseFilename()}_${presentedCurrency}` : baseFilename());

  const guardEmpty = () => {
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      toast.info('Nothing to export: the report has no data for this period.');
      return true;
    }
    return false;
  };

  const handleCSVExport = async () => {
    if (guardEmpty()) return;
    setExporting('csv');
    try {
      const rows = withComparisonRows(formatReportForExport(data, reportType));
      if (!rows || rows.length === 0) {
        toast.info('Nothing to export: the report has no data for this period.');
        return;
      }
      downloadCSV(stampCurrency(rows), baseFilenameCcy());
      toast.success(`CSV downloaded${presentedCurrency ? ` (${presentedCurrency})` : ''}`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(null);
    }
  };

  const handleExcelExport = async () => {
    if (guardEmpty()) return;
    setExporting('excel');
    try {
      const rows = withComparisonRows(formatReportForExport(data, reportType));
      if (!rows || rows.length === 0) {
        toast.info('Nothing to export: the report has no data for this period.');
        return;
      }
      await downloadExcel(stampCurrency(rows), baseFilenameCcy(), reportName, fxNote ? [
        { Field: 'Report', Value: reportName },
        { Field: 'Presentation currency', Value: presentedCurrency },
        { Field: 'Book currency', Value: data.original_currency },
        { Field: 'FX rate', Value: `1 ${data.original_currency} = ${data.fx_rate} ${presentedCurrency}` },
        { Field: 'Note', Value: fxNote },
      ] : null);
      toast.success(`Excel downloaded${presentedCurrency ? ` (${presentedCurrency})` : ''}`);
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(null);
    }
  };

  // PDF = exact replica of the on-screen report (same engine as Insights exports).
  const handlePDFExport = async () => {
    if (guardEmpty()) return;
    const node = captureRef?.current;
    if (!node) {
      toast.error('The report is not on screen yet. Wait for it to load, then export again.');
      return;
    }
    setExporting('pdf');
    try {
      const capture = await captureElement(node, { settleMs: 400 });
      const periodLabel = (['balance_sheet', 'trial_balance', 'aged_receivables', 'aged_payables'].includes(reportType) && period?.end)
        ? `As at ${fmtDay(period.end)}`
        : (fmtPeriod(period) || '');
      const subtitle = [businessName, presentedCurrency ? `Currency ${presentedCurrency}` : '', (comparisonData || multiColumns) ? `vs ${comparisonLabel}` : '']
        .filter(Boolean).join(' • ');
      savePdf(buildWysiwygPdf([{ capture, title: reportName, subtitle, period: periodLabel }], { footerNote: fxNote }), `${baseFilenameCcy()}.pdf`);
      toast.success(`PDF downloaded${presentedCurrency ? ` (${presentedCurrency})` : ''}`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={!!exporting}
          data-testid="report-export-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Export
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Export Format</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleExcelExport}
          disabled={exporting === 'excel'}
          className="cursor-pointer"
          data-testid="export-excel-btn"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
          <span className="flex-1">Excel (.xlsx)</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleCSVExport}
          disabled={exporting === 'csv'}
          className="cursor-pointer"
          data-testid="export-csv-btn"
        >
          <FileText className="w-4 h-4 mr-2 text-green-600" />
          <span className="flex-1">CSV</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handlePDFExport}
          disabled={exporting === 'pdf'}
          className="cursor-pointer"
          data-testid="export-pdf-btn"
        >
          <FileType className="w-4 h-4 mr-2 text-red-600" />
          <span className="flex-1">PDF</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ReportExportButton;
